import {
  ChangeTypeOfKeys,
  ClientDocumentKind,
  DocumentCategory,
  DueDateRuleType,
  FileTags,
  FilingStatus,
  LifecycleStage,
  StartDateRuleType,
  TaskDateAnchor,
  TaxpayerType,
  UploadFor,
  WorkflowScheduleFrequency,
} from '@/core/const';
import { ContactRelationship } from '@/pages/taxpayers/const';
import { type AnalysisRanFor } from '@/pages/shared/run-metadata/ranForSummary';
import { type QuestionnaireSchema } from '@/pages/questionnaires/schema';
import {
  SetDataByKeyValuePair,
  SetDataByMethod,
  SetDataByObject,
} from '@/core/const';

type DateTime = string;

export type Nullable<T> = T | null;
export type FileTagKey = (typeof FileTags)[keyof typeof FileTags];
export type User = {
  pid: string;
  first_name: string;
  last_name: string;
  full_name: string;
  name: string;
  email: string;
  role: string;
  roleType: string;
  roles?: string[];
  permissions?: string[];
  created_at: string;
  updated_at: string;
  email_verified_at: null | string;
  two_factor_confirmed_at: null | string;
  two_factor_recovery_codes: null | string;
  two_factor_secret: null | string;
  profile_photo_url: string;
  has_profile_photo: boolean;
  is_two_factor_enabled: boolean;
  teams: Team[];
  firms: Firm[];
  jobFunctions?: JobFunction[];
  invited?: boolean;
  deactivated: boolean;
  settings: {
    [UserSettingsCategory.user_activities]?: {
      [key: string]: string;
    };
    [UserSettingsCategory.task_notifications]?: {
      [key: string]: string;
    };
  };
  activitiesViewedAt: string;
};

export enum UserSettingsCategory {
  user_activities = 'user_activities',
  task_notifications = 'task_notifications',
}

export enum UserActivitySettings {
  daily_summary_email = 'daily_summary_email',
  team_activities = 'team_activities',
}

export enum TaskNotificationSettings {
  task_assigned = 'task_assigned',
}

export type Permission = {
  name: string;
  type: RoleType;
  description?: string;
};

export enum RoleType {
  systemAdmin = 'system_admin',
  team = 'team',
  superAdmin = 'super_admin',
}

export type Role = {
  id: number;
  name: string;
  guard_name?: string;
  created_at: string;
  updated_at: string;
  userCount?: number;
  permissions: Permission[];
  type: RoleType;
  description?: string;
};

export type RoleFormValues = ChangeTypeOfKeys<
  Pick<Role, 'name' | 'permissions' | 'type' | 'description'>,
  'permissions',
  string[]
>;

export type InertiaSharedProps<T extends object> = T & {
  auth: {
    user: User;
  };
  taxpayersIds: string[];
  features: Record<string, boolean>;
  /** TaskState::label() per state; the renamed names (HWEPD-10555). */
  taskStateLabels?: Record<TaskState, string>;
  taskHoldReasons?: TaskHoldReasonOption[];
};

export type FeatureFlagScopeType = 'global' | 'firm' | 'role' | 'user';

export type FeatureFlagScope = {
  pid: string;
  scopeType: FeatureFlagScopeType;
  scopeLabel: string;
  enabled: boolean;
};

/**
 * Result of the developer console's live codebase scan for a flag key.
 * `scanned: false` means the scan could not run — which is NOT the same as
 * finding no references, and must never read as "safe to remove".
 */
export type FeatureFlagReferenceScan = {
  scanned: boolean;
  matches: string[];
};

export type FeatureFlag = {
  pid: string;
  key: string;
  label: string;
  description: string | null;
  defaultEnabled: boolean;
  createdAt: string;
  scopes: FeatureFlagScope[];
};

export type Session = {
  ip_address: string;
  id: number;
  is_current_device: boolean;
  agent: {
    is_desktop: boolean;
    platform: string;
    browser: string;
  };
  last_active: DateTime;
};

export type Team = {
  created_at: string;
  name: string;
  pid: string;
  updated_at: string;
  members: User[];
  taxpayers: Taxpayer[];
  contacts: Contact[];
  userInvitations: UserInvite[];
  advisorId: string;
  advisorFullName: string;
  firm: Firm;
  taxpayers_count?: number;
  members_count?: number;
  contacts_count?: number;
  hubspotId: string | undefined;
  advisorDisplayName: string | null;
  advisorAvatar?: string;
  taxpayersCount?: number;
  profile_photo_url?: string;
};

export type EditTeamFormData = Pick<Team, 'name' | 'advisorDisplayName'> & {
  advisorId: string | undefined;
  firmPid: string | undefined;
};

export type SetInertiaTeamFormData = SetDataByObject<EditTeamFormData> &
  SetDataByMethod<EditTeamFormData> &
  SetDataByKeyValuePair<EditTeamFormData>;

export enum AccountStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum AccountStatusLabel {
  ACTIVE = 'Active account',
  INACTIVE = 'No account',
}

export type UserInvite = {
  pid: string;
  email: string;
  name: string;
  role: string;
  teams: Team[];
  invited: boolean;
};

export enum InviteStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  EXPIRED = 'expired',
  REVOKED = 'revoked',
}

export type ContactInvite = {
  pid: string;
  contact_id: string;
  contact_email: string;
  expires_at: Date;
  accepted_at: Date | null;
  revoked_at: Date | null;
  deleted_at: Date | null;
  created_at: Date;
  updated_at: Date;
  invite_status: InviteStatus;
  invite_url: string;
};

export type Contact = {
  pid: string;
  hw_user_pid: string;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  phone: string | null;
  photo_profile_url: string;
  created_at: string;
  updated_at: string;
  deleted_at: Date | null;
  taxpayers: Taxpayer[];
  hubspot_id: number;
  hubspot_url: string;
  // TODO: This could be temporary solution only
  contact_owner?: string;
  teams: Team[];
  firms: Firm[];
  accountStatus: AccountStatus;
  contactInvite: ContactInvite | null;
  disabledNotificationsTaxpayersPids: string[];
};

export type ContactWithRelationship = Contact & {
  contact_relationship: ContactRelationship;
};

/**
 * HubSpot email AI summaries (#3352) — the taxpayers.contact-emails payload.
 * syncStatus drives the last-email card: pending → skeleton, synced with a
 * lastEmail → summary card, synced without one → "No email on record",
 * failed → quiet error (the per-card Sync button recovers it).
 */
export type EmailSyncStatus = 'pending' | 'synced' | 'failed';

export type EmailDirection = 'SENT' | 'RECEIVED' | 'FORWARDED';

export type EmailSummary = {
  hubspotEmailId: number;
  summary: string | null;
  subject: string | null;
  occurredAt: string;
  direction: EmailDirection | null;
  /** Firm-side participant resolved for the viewing firm; null omits "with …". */
  firmSidePerson: string | null;
  contactName: string | null;
  /** Opens the email in the card contact's HubSpot timeline; null if no contact could be resolved. */
  hubspotUrl: string | null;
};

export type ContactEmailEntry = {
  syncStatus: EmailSyncStatus;
  syncedAt: string | null;
  lastEmail: EmailSummary | null;
};

export type RecentComms = {
  summary: string;
  generatedAt: string;
  latestEmail: EmailSummary | null;
};

export type ContactEmailsResponse = {
  /** Keyed by contact pid. */
  contacts: Record<string, ContactEmailEntry>;
  /** Null until the client's first roll-up has been generated; absent on the proposal endpoint. */
  recentComms?: RecentComms | null;
};

/** Payload of the ContactEmailsSynced broadcast (contactPid null for roll-ups). */
export type ContactEmailsSyncedEvent = {
  taxpayerPid: string;
  contactPid: string | null;
};

export type FiledReturnTypeOption = { value: string; label: string };

export type FiledTaxpayerProps = {
  sent: boolean;
  suggestedReturnType: string | null;
  returnTypes: FiledReturnTypeOption[];
  taxYear: number;
};

export type FiledSeverity = 'CRITICAL' | 'MEDIUM' | 'LOW';

export type FiledBinderItem = {
  id: string;
  item: string;
  formType: string | null;
  issuer: string | null;
  taxYear: number | null;
  severity: string;
  reason: string;
};

export type FiledBinderTask = {
  id: string;
  status: string;
  errorMessage: string | null;
  subTasks: { type: string; status: string }[];
};

export type FiledBinderProps =
  | { error: true }
  | {
      error: false;
      assessment: {
        status: string;
        reason: string | null;
      };
      openItems: FiledBinderItem[];
      latestTask: FiledBinderTask | null;
    };

export type FiledTaxpayerUpdatedEvent = {
  taxpayerPid: string;
  event: string;
  taskType: string | null;
  subTaskType: string | null;
  status: string | null;
  errorMessage: string | null;
  occurredAt: string | null;
};

export type FiscalYearEnd = {
  month: number;
  day: number;
};

export type Taxpayer = {
  pid: string;
  name: string;
  type: TaxpayerType;
  filingStatus?: FilingStatus;
  lifecycleStage: LifecycleStage | null;
  fiscalYearEnd: FiscalYearEnd | null;
  created_at: string;
  updated_at: string;
  users: User[];
  contacts: ContactWithRelationship[];
  teams: string[];
  firm: Firm | null;
  billingContact: Contact | null;
  associatedTeams: Team[];
  advisor: User | null;
  signers: Contact[];
  engagementLetters: EngagementLetter[];
  lastProposalLabel: string;
  lastFilingEngagementLabel: string | null;
  lastFilingEngagementPid: string | null;
  isArchived: boolean;
  taxpayerGroups: { pid: string; name: string }[];
  tags?: WorkflowTag[];
};

export type TaxpayerGroup = {
  pid: string;
  name: string;
  members: Taxpayer[];
  contacts: Contact[];
};

export type RelatedClient = {
  pid: string;
  name: string;
  type: TaxpayerType;
  filingStatus: FilingStatus | null;
};

export type ClientGroup = {
  pid: string;
  name: string;
  membersCount: number;
  contactsCount: number;
  relatedClients: RelatedClient[];
};

export type Pagination = {
  total: number;
  page: number;
  pageSize: number;
};

export type NoteData = {
  pid: string;
  title: string;
  body: string | null;
  noteDate: string;
  createdAt: string;
  updatedAt: string;
  author: { pid: string; name: string };
  // pid/name are null when the source client has since been archived.
  notable: {
    type: 'taxpayer' | 'workflow';
    pid: string | null;
    name: string | null;
  };
  // Every client record the note is filed under, origin first (HWEPD-10164).
  appliesTo?: {
    pid: string;
    name: string;
    entityType: string;
    isOrigin: boolean;
  }[];
  files: {
    pid: string;
    originalName: string;
    size: number;
    contentType: string;
  }[];
  // False when the note was shared in from a client the viewer cannot reach:
  // edit/delete/publish are hidden, mirroring the backend's write gate. Absent
  // on single-note responses, where it is always writable (HWEPD-10164).
  canWrite?: boolean;
  isPublished: boolean;
  publishedAt: string | null;
  publishedBy: { pid: string; name: string } | null;
  // Present only in the profile wizard's combined (multi-member) note listing.
  memberPid?: string;
  memberName?: string;
};

export type TaxpayerDocument = {
  pid: string;
  name: string;
  status: InvoiceStatus | EngagementLetterStatus;
  statusChangedAt: string;
  type: 'invoice' | 'engagementLetter';
  invoiceType?: string;
  documentUrl?: string;
};

export enum TaskDescriptorType {
  body = 'body',
  cta = 'cta',
  title = 'title',
}

export enum TaskDescriptorState {
  blocked = 'blocked',
  completed = 'completed',
  default = 'default',
  open = 'open',
  pending = 'pending',
}

export enum SocratesTaskState {
  draft = 'draft',
  blocked = 'blocked',
  completed = 'completed',
  open = 'open',
  pending = 'pending',
  in_progress = 'in_progress',
  dismissed = 'dismissed',
  accepted = 'esign_acceptance',
  submitted = 'esign_submitted',
  paper_filing = 'paper_filing',
}

export type EfileState =
  | 'completed'
  | 'sent'
  | 'in_progress'
  | 'draft'
  | 'no_draft';

export enum FileUploadedByKind {
  harnessTax = 'HARNESS_TAX',
  client = 'CLIENT',
  auto_generated = 'AUTO_GENERATED',
}

type Prediction = {
  value?: string;
  confidence: number;
};

export type PredictionMetadata = {
  analyzable: boolean;
  documentType?: string;
  issuer?: Prediction;
  accountNumber: Prediction;
  fileCategory: string;
  taxYear: Prediction;
  taxpayerName: Prediction;
  dates: string[];
  amounts: string[];
  language: string;
  explanation: string;
};

/**
 * The latest recorded action on a document, as the Status column shows it
 * (HWEPD-10390). `label` is the state the document is in (New / Opened /
 * Downloaded / In Drive) and `action` the event that put it there, both worded by
 * `App\Enums\DocumentActionType` so the vocabulary lives in one place. `action`,
 * `actorName` and `occurredAt` are all absent on a client upload nobody at the
 * firm has touched: nothing was recorded, because the client is not a firm user.
 */
export type DocumentStatus = {
  label: string;
  /** Raw `DocumentActionType` value, which picks the design's state icon. */
  actionType: string | null;
  action: string | null;
  actorName: string | null;
  occurredAt: string | null;
  /** The `New` state, which the design marks with a gold dot. */
  isNew: boolean;
  /** A client upload nobody at the firm has touched: the row stands out. */
  needsReview: boolean;
};

export type File = {
  associations: {
    entityPid: string;
    entityType: string;
  }[];
  pid: string;
  createdAt: string;
  name: string;
  downloadLink: string;
  uploadedByKind: FileUploadedByKind | null;
  uploadedByLabel: string;
  aeUserPid: string | null;
  aeTaxpayerPid: string | null;
  tags: FileTagKey[];
  // The Harness category the first tag maps to.
  category?: DocumentCategory | null;
  taxYear: number;
  location: string;
  engagementName: string;
  engagementPid: string;
  workflowPid?: string;
  // The workflow an internal-only file was uploaded straight to (HWEPD-10436).
  attachedWorkflowPid?: string | null;
  workflowName?: string;
  predictionMetadata?: PredictionMetadata;
  contentType?: string;
  uploadFor: UploadFor;
  syncedAt?: string;
  syncStatus?: string;
  parentFolderId?: string;
  cloudStorageType?: string;
  // Present only in the profile wizard's combined (multi-member) file listing.
  memberPid?: string;
  memberName?: string;
  sourceLabel?: string;
  // Present only in the flat documents-tab listing (FilesFilter.allFiles).
  locationType?: 'engagement' | 'workflow' | 'permanent' | 'analysis';
  kind?: ClientDocumentKind;
  // Which store the row came from. Internal-only files live in the AE file
  // store and are never mirrored to Socrates (HWEPD-10256), so their row
  // actions take a different route.
  store?: 'socrates' | 'internal';
  // Internal files a task or an analysis still depends on cannot be deleted
  // from the documents list.
  canDelete?: boolean;
  // Present only on the client Documents tab, and only while the
  // `document_action_status` flag is on. Null for a document with nothing
  // recorded — the Status column stays empty for those (HWEPD-10390).
  documentStatus?: DocumentStatus | null;
};

export type SuggestedUpload = {
  pid: string;
  dismissedAt: string;
  notes: string[];
  file: File | null;
  checkedAt: string;
  displayName: string;
};

export type SocratesPaginatedResult<T> = {
  count: number;
  results: T[];
  allMatchingPids: string[];
};

/**
 * The client Documents tab's file page. Carries the tab's "Needs review" total
 * alongside the rows (HWEPD-10391): client uploads nobody at the firm has
 * opened, counted over the whole tab rather than the page on screen. Absent
 * while the `document_action_status` flag is off.
 */
export type ClientDocumentsResult = SocratesPaginatedResult<File> & {
  needsReviewCount?: number;
};

export type SigningStatus = {
  firstName: string;
  hasCompleted: boolean;
  lastName: string;
  role: null;
  sessionExpiry: string;
  sessionId: string;
  signatureDate: string | null;
};

export type PandaDocDocument = {
  id: number;
  pid: string;
  name: string;
  documentState: string;
  documentId: string;
  signingStatus: {
    [key: string]: SigningStatus;
  };
  document: null;
  kbaEnabled: boolean;
};
export enum UpdateSource {
  command = 'command',
  advisor = 'advisor_api',
  consumer = 'consumer_api',
}

export type SocratesTask = {
  pid: string;
  state: SocratesTaskState;
  taskType: TaskTypeName;
  dueDate: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string;
  descriptors: TaskDescriptor[];
  files: File[];
  variation?: TaskTypeVariation;
  pandadocDocuments: PandaDocDocument[];
  // v2 tasks
  signers: {
    email: string;
    firstName: string;
    lastName: string;
    signed: boolean;
    signatureDate: string;
    signingUrl: string;
  }[];
  updateSource: UpdateSource;
  isAdhocTask?: boolean;
  isAwaitingK1Task?: boolean;
  waitingForDocumentTypes?: string[];
  waitingForDocumentsMessage?: string;
  documentState?: string;
  documentName?: string;
  documentId?: string;
  invoiceId?: number;
  kbaEnabled?: boolean;
};

export type DocumentSigningStatus = {
  date: string;
  taskPid: string;
  signingStatusSummary: { [key: string]: SigningStatus };
  state: string;
  variation: TaskTypeVariation;
  documentId?: string;
  documentState?: string;
  documentName?: string;
};

export type Engagement = {
  aeTaxpayerPid: string;
  aeContactPid: string;
  dueDate: string;
  createdAt: string;
  clientActivity: string;
  name: string;
  engagementType: EngagementTypes;
  efileDocumentSigning: null | DocumentSigningStatus;
  pid: string;
  tasks: SocratesTask[];
  taxpayer: Taxpayer;
  assignee?: User;
  clientFileUploads: {
    state: string;
    date: string;
    indicateWaitingForK1: boolean;
    waitingForDocumentsMessage: string;
    source: UpdateSource;
  };
  questionnaire: {
    state: string;
    date: string;
    source: UpdateSource;
  };
  aeUserPid?: string;
  aeAdvisorTeamPid: string;
  openTasks: number;
  completedTasks: number;
  year: number | null;
  quarter: number | null;
  suggestedUploadsNotesCount: number;
  meta?: {
    advisorTeamName: string;
  };
  displayName?: string;
  customName?: string;
  clientType?: string;
  taJobStatus?: string;
  taJobStatusTimestamp?: string;
};

export type ApiInvoice = {
  id: string;
  subject: string;
  due_date: string;
};

export type TaskInvoice = {
  id: string;
  subject: string;
  note: string;
  dueDate: string;
};

export enum EngagementCategory {
  Filing = 'filing',
  Accounting = 'accounting',
  PlanningConsulting = 'planning/consulting',
}

export enum EngagementTypes {
  EVALUATION = 'evaluation',
  SERVICE = 'service',
  AUDIT = 'audit',
  FILING_990 = '990',
  FILING_1040 = '1040',
  FILING_1041 = '1041',
  FILING_1065 = '1065',
  FILING_1120 = '1120',
  FILING_1120_S = '1120-S',
  FILING_1120_F = '1120-F',
  FILING_5500 = '5500',
  FILING_5227 = '5227',
  OTHER_FILING = 'other filing',
  QUARTERLY_ESTIMATES = 'quarterly estimates',
  AD_HOC_PLANNING = 'ad hoc planning',
  EQUITY_SESSION = 'equity session',
  INTRODUCTION = 'introduction',
  RENEWAL = 'renewal',
  BOOKKEEPING = 'bookkeeping',
  OTHER_ACCOUNTING = 'other accounting',
  EVALUATION_UPLOAD = 'evaluation upload',
}

export type EngagementTemplate = {
  name: string;
  pid: string;
  tasks: SocratesTask[];
  category: EngagementCategory;
  engagementType: EngagementTypes;
  clientType: string;
  dueDate: string | null;
};

export type TaskDescriptor = {
  descriptorType: string; // deprecated
  descriptor_type: string;
  state: string;
  value: string;
};

export type TemplateTask = {
  pid: string;
  taskType: string;
  variation?: string;
  descriptor?: TaskDescriptor[];
};

export enum TaskTypeName {
  document_signing = 'document_signing',
  file_download = 'file_download',
  file_import = 'file_import',
  meeting = 'meeting',
  payment = 'payment',
  questionnaire = 'questionnaire',
  scheduling = 'scheduling',
  efile_document_signing = 'efile_document_signing',
  invoice = 'invoice',
}

export enum TaskTypeVariation {
  auto_payment_info = 'auto_payment_info',
  existing_client_tax_questionnaire = 'existing_client_tax_questionnaire',
  final_return = 'final_return',
  generic_esign = 'generic_esign',
  extension = 'extension',
  irs_receipt = 'irs_receipt',
  new_client_tax_questionnaire = 'new_client_tax_questionnaire',
  prior_year_returns = 'prior_year_returns',
  tax_questionnaire = 'tax_questionnaire',
  upload_categories = 'upload_categories',
}

export type TaskType = {
  pid: string;
  name: TaskTypeName;
  descriptors: TaskDescriptor[];
  variation?: TaskTypeVariation;
};

export type HarvestIntegration = {
  id: number;
  firmId: number;
  connectedBy: string;
  connectedAccountEmail: string;
  connectedAt: string;
};

export type FiledIntegration = {
  workspaceName: string;
  connectedAccountEmail: string;
  connectedBy: string | null;
  connectedAt: string;
};

export type FirmUser = {
  pid: string;
  name: string;
  email: string;
  profile_photo_url: string;
};

export type Firm = {
  name: string;
  displayName: string;
  pid: string;
  teams: Team[];
  users?: FirmUser[];
  pandaDocWorkspacePath: string | null;
  hubspot_id: string | null;
  taxassist_id: string | null;
  cloudStorageIntegrations?: FirmCloudStorageIntegration[];
  harvestIntegration?: HarvestIntegration;
  filedIntegration?: FiledIntegration | null;
  filedConnected?: boolean;
  isHarvestEnabled: boolean;
  isAdvisoryFirm: boolean;
  isMeasurementEnabled: boolean;
  jobFunctions?: JobFunction[];
};

export type PandaDocWebhookHealthStatus =
  | 'healthy'
  | 'inactive_subscriptions'
  | 'no_subscriptions'
  | 'not_configured'
  | 'unknown';

export type PandaDocWebhookHealth = {
  status: PandaDocWebhookHealthStatus;
  inactiveCount: number;
  subscriptionCount: number;
  isSharedSandboxKey: boolean;
  errorMessage: string | null;
};

export type JobFunction = {
  pid: string;
  name: string;
  isDefault?: boolean;
};

export type FirmCloudStorageIntegration = {
  id: number;
  connectedTo: string | null;
  connectedBy: string | null;
  connectedAt: string | null;
  taxpayersSetupCount: number;
};

export {};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Canny: any;
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface URLSearchParams {
    readonly size: number;
  }
}

export enum InvoiceStatus {
  draft = 'draft',
  sent = 'sent',
  paid = 'paid',
  deleted = 'deleted',
}

export enum EngagementLetterStatus {
  draft = 'draft',
  sent = 'sent',
  signed = 'signed',
}

export type EngagementLetter = {
  pid: string;
  documentUrl: string;
  signatoryUrl: string;
  status: EngagementLetterStatus;
  statusChangedAt: string;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  name: string;
  proposalPid?: string;
  manuallySigned: boolean;
};

export type Retainer = Omit<Invoice, 'type'> & {
  type: 'retainer';
};

export type FinalInvoice = Omit<Invoice, 'type'> & {
  type: 'finalInvoice';
  invoiceId: number;
};

export type Invoice = {
  pid: string;
  status: InvoiceStatus;
  amount: number;
  createdAt: string;
  documentUrl: string;
  statusChangedAt: string;
  type: 'retainer' | 'invoice' | 'finalInvoice';
  engagementPid?: string;
  proposalPid?: string;
  documentClientUrl?: string;
  invoiceId: number;
  subject: string;
  dueDate: string;
  isEditable: boolean;
  isSendable: boolean;
  billingContactFullName: string;
};

export type InvoiceShortInfo = Pick<
  Invoice,
  | 'pid'
  | 'subject'
  | 'type'
  | 'statusChangedAt'
  | 'documentUrl'
  | 'documentClientUrl'
  | 'status'
  | 'engagementPid'
  | 'proposalPid'
  | 'isSendable'
  | 'invoiceId'
>;

export type ContactWithLabel = Contact & { label: ('signer' | 'billing')[] };
export type Signer = Partial<Contact> & {
  name: string;
  pid: string;
  signatoryUrl: string;
  signedAt?: string;
};

export type ProposalListType = {
  pid: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  firmPid: string;
  teams: Team[];
  taxpayers: Taxpayer[];
  billingContactFullName: string | null;
  taxYear: number;
  engagementLetterStatus: {
    status: EngagementLetterStatus;
    statusChangedAt: string;
    signers: Signer[];
    documentUrl: string;
    manuallySigned?: boolean;
  } | null;
  retainerAlreadySent: boolean;
  retainerAmount: number;
  retainerStatus: {
    status: InvoiceStatus;
    statusChangedAt: string;
    documentUrl?: string;
  } | null;
  finalInvoiceStatus: {
    invoiceId: number;
    status: InvoiceStatus;
    statusChangedAt: string;
    documentUrl?: string;
    documentClientUrl?: string;
  } | null;
  user: User;
  type: ProposalType;
  status: ProposalStatus;
};

export type ProposalShortData = Pick<
  ProposalListType,
  | 'pid'
  | 'engagementLetterStatus'
  | 'retainerStatus'
  | 'finalInvoiceStatus'
  | 'taxYear'
>;

export type Proposal = {
  pid: string;
  name: string;
  hubspotUrl: string;
  projectType: string;
  taxYear: number;
  createdAt: string;
  updatedAt: string;
  projections: boolean;
  fee: number;
  totalDealAmount: number;
  xpitax: boolean;
  taxpayers: Taxpayer[];
  teams: Team[];
  assignee?: User;
  firmPid: string;
  contacts: ContactWithLabel[];
  signers: Signer[];
  billingContact: Contact;
  engagementLetter: EngagementLetter | null;
  engagementLetterDeliverables: string | null;
  deliverables: string[];
  retainerAlreadySent: boolean;
  retainer: Retainer | null;
  finalInvoice: FinalInvoice | null;
  files: ProposalFile[];
  percentage: number;
  status: ProposalStatus;
  retainerAmount: number;
  type: ProposalType;
};

export enum ProposalType {
  LEAD = 'lead',
  RENEWAL = 'renewal',
}

export enum ProposalStatus {
  CLOSED_LOST = 'closed_lost',
  CLOSED_WON = 'closed_won',
  OPEN = 'open',
}

export type ProposalFile = {
  pid: string;
  name: string;
  url: string;
  completedAt: string;
};

// todo: once retainer gets statusChangedAt, add it to both Retainer and EngagementLetter and ProposalTask
export type ProposalTask = {
  id: number;
  name: string;
  status:
    | InvoiceStatus
    | EngagementLetterStatus
    | 'in progress'
    | 'done'
    | null;
  createdAt: string | null;
  documentUrl: string | null;
  dueDate: string | null;
  statusChangedAt: string | null;
  manuallySigned?: boolean;
  type: keyof LabelMapType;
} & (
  | { type: 'retainer'; retainerAmount: number; invoiceId: number | null }
  | { type: 'finalInvoice'; invoiceId: number | null }
  | { type: 'final'; invoiceId: number | null }
  | {
      type: 'engagementLetter';
      signatoryUrl: string | null;
    }
);

export enum ActivityEntity {
  engagement = 'engagement',
  taxpayer = 'taxpayer',
  workflow = 'workflow',
}

export enum ActivityType {
  fileUploaded = 'file_uploaded',
  taxQuestionnaireCompleted = 'tax_questionnaire_completed',
  bankQuestionnaireCompleted = 'bank_questionnaire_completed',
  finalEfileAuthCompleted = 'final_efile_auth_completed',
  extensionEfileAuthCompleted = 'extension_efile_auth_completed',
  workflowTaskCompleted = 'workflow_task_completed',
  analysisCompleted = 'analysis_completed',
}

export type Activity = {
  pid: string;
  name: string;
  entityPid: string;
  filesCount: number;
  createdAt: string;
  entity: ActivityEntity;
  entityType: string | null;
  type: ActivityType;
  firmPid: string;
  // Workflow rows only: the sub-entity label read from the activity's metadata,
  // which isn't guaranteed to carry it.
  taskTitle?: string | null;
  // Persisted BlueprintTaskType value; 'questionnaire' when a client submitted one.
  taskType?: string | null;
  requestName?: string | null;
  // Workflow rows only: whether the viewer can reach the workflow's taxpayer, and so
  // whether workflows.show would render rather than 404.
  isNavigable?: boolean;
  // Analysis rows only: the run's terminal status, so a failed run reads as
  // failed rather than implying a result is waiting.
  analysisStatus?: string | null;
};

export type LabelMapType = {
  retainer: {
    [key in InvoiceStatus]: {
      view: string;
      send?: string;
      reminder?: string;
    };
  };
  final: {
    [key in InvoiceStatus]: {
      view: string;
      send?: string;
      reminder?: string;
    };
  };
  engagementLetter: {
    [key in EngagementLetterStatus]: {
      view: string;
      send?: string;
      reminder?: string;
    };
  };
  finalInvoice: {
    [key in InvoiceStatus]: {
      view: string;
      send?: string;
      reminder?: string;
    };
  };
};

export type TaskTypeV2 = {
  pid: string;
  type: TaskTypeName;
  name: string;
  note: string;
  variation?: TaskTypeVariation;
  descriptors: TaskDescriptor[];
};

export type TaskTypeData = TaskTypeV2 & {
  dueDate: Date | undefined;
};

export type CreateEngagementFormData = {
  taxpayerPids: string[];
  serviceTemplatePid: string;
  dueDate: Date | undefined;
  redirectTo: string | null;
  year: number | undefined;
  quarter: number | undefined;
  month: number | undefined;
  clientFacingLabel: string;
  tasks: TaskTypeData[];
};

export type ServiceTemplate = {
  pid: string;
  name: string;
  taskTypes: TaskTypeV2[];
};

export type Service = {
  pid: string;
  name: string;
  templates: ServiceTemplate[];
};

export type PropertyOption = {
  label: string;
  value: string;
  display_order: number;
  hidden: boolean;
};

export type PropertyDefinition = {
  name: string;
  label: string;
  type: string;
  field_type: string;
  description: string;
  group_name: string;
  options: PropertyOption[];
};

export enum ProposalProjectType {
  FIXED_FEE = 'Fixed Fee',
  TIME_AND_MATERIALS = 'Time and Materials',
}

/**
 * The taxpayer fields the proposal form carries.
 *
 * Inertia's `useForm` derives a dot-notation key for every nested field, so the
 * model it is given has to be acyclic — the full `Taxpayer` graph loops back on
 * itself (taxpayer -> contacts -> taxpayers) and TypeScript cannot expand it.
 * The form only reads these fields. ProposalUpdateRequest validates `taxpayer.pid`
 * and `taxpayer.name`; ProposalCreateRequest only requires the array.
 */
export type ProposalFormTaxpayer = Pick<
  Taxpayer,
  'pid' | 'name' | 'lifecycleStage'
> & {
  signers: Pick<Contact, 'pid'>[];
};

export type ProposalFormData = {
  taxpayer: ProposalFormTaxpayer | null;
  projectType: ProposalProjectType | string;
  feeEstimate: number | null;
  totalDealAmount: number | null;
  retainerPercentage: number | null;
  usingOffshoreSolutions: boolean;
  engagementLetterDeliverables: string;
  federalDeliverables: PropertyOption[];
  stateDeliverables: PropertyOption[];
  quarterlyEstimates: boolean;
  taxYear: number | null;
  isSelectFilingDeliverables: boolean;
  engagementLetterAlreadySigned: boolean;
  retainerAlreadySent: boolean;
  type: ProposalType | null;
  secondaryTemplate: boolean;
};

export type TemplateIds = {
  primary: {
    single_signatory: string;
    single_signatory_7216: string;
    dual_signatory: string;
    dual_signatory_7216: string;
  };
  secondary: {
    single_signatory: string;
    single_signatory_7216: string;
    dual_signatory: string;
    dual_signatory_7216: string;
  };
};

/** A template's due-date rule as stored; null means no due date. */
export type DueDateRule = {
  type: DueDateRuleType;
  month?: number;
  day?: number;
  days?: number;
} | null;

/** A template's start-date rule as stored; null means at creation. */
export type StartDateRule = {
  type: StartDateRuleType;
  month?: number;
  day?: number;
  days?: number;
} | null;

/** The recurrence a workflow created from a template starts with; null means it does not repeat. */
export type RepeatRule = {
  frequency: WorkflowScheduleFrequency;
  /** Total occurrences, the first one included; absent repeats forever. */
  count?: number;
} | null;

/** The template editor's tabs; the Recurrence tab exists only under the recurrence flag. */
export type TemplateEditorTab = 'workflow' | 'recurrence';

/** The create form's tabs; the Recurrence tab exists only under the recurrence flag. */
export type CreateFormTab = 'workflow' | 'recurrence';

/** A template task's due date counted from a workflow date; null means no date. */
export type TaskDateOffset = {
  anchor: TaskDateAnchor;
  days: number;
} | null;

/** How a task's due date was set: from its template offset, or by hand. */
export type TaskDueDateSource = 'computed' | 'manual';

/** What a template's rules and the advisor's own dates settle on for the create form. */
export type BlueprintResolvedDates = {
  /** The start date the form will use: the rule's unless the advisor typed one. */
  startDate: string;
  startFromRule: boolean;
  /** The start rule's plain arithmetic before the past clamp; null unless the start follows it. */
  plainStartDate: string | null;
  dueDate: string | null;
  dueFromRule: boolean;
  /** The due rule's plain arithmetic before the weekend shift and the past clamp; null unless the due date follows it. */
  plainDueDate: string | null;
  startRule: StartDateRule;
  dueRule: DueDateRule;
  avoidWeekends: boolean;
  /** Computed task due dates keyed by task blueprint pid. */
  tasks: Record<string, string | null>;
};

/** The kind of work a template covers. Mirrors App\Enums\WorkflowType. */
export type WorkflowType = 'advisory' | 'tax' | 'accounting' | 'other';

export type WorkflowTypeOption = { value: WorkflowType; label: string };

export type WorkflowBlueprint = {
  pid: string;
  name: string;
  description: string | null;
  clientDescription: string | null;
  workflowType: WorkflowType;
  startRule: StartDateRule;
  dueRule: DueDateRule;
  repeatRule: RepeatRule;
  avoidWeekends: boolean;
  taskBlueprintsCount: number;
  tags?: WorkflowTag[];
  createdAt: string;
  updatedAt: string | null;
};

export type WorkflowCurrentTask =
  | { actionRequired: true; onHold?: undefined }
  | { actionRequired: false; onHold: true }
  | {
      actionRequired: false;
      onHold?: undefined;
      taskPid: string;
      title: string;
      dueDate: string | null;
      ownerType: 'client' | 'team';
      ownerName: string | null;
      ownerPid: string | null;
    };

export type WorkflowTag = {
  id: number;
  name: string;
  color: string | null;
};

export type RecurrenceSettings = {
  enabled: boolean;
  frequency: WorkflowScheduleFrequency;
  /** Total occurrences, the one created now included; null repeats forever. */
  repeatCount: number | null;
  avoidWeekends: boolean;
};

export type ScheduleOccurrence = {
  name: string;
  startsOn: string;
  dueOn: string | null;
};

export type SchedulePreview = {
  creatingNow: ScheduleOccurrence;
  upcoming: ScheduleOccurrence[];
  summary: string;
};

export type WorkflowScheduleStatus = 'active' | 'completed' | 'stopped';

/** A workflow a schedule created, as the schedule JSON lists it. */
export type ScheduleWorkflow = {
  pid: string;
  name: string;
  occurrenceNumber: number | null;
  startDate: string | null;
  dueDate: string | null;
  completedAt: string | null;
  canceledAt: string | null;
};

/** The shape every schedule surface reads: the drawer dialog, the client card and the schedule endpoints. */
export type WorkflowSchedule = {
  pid: string;
  baseName: string;
  frequency: WorkflowScheduleFrequency;
  /** Total occurrences, the first included; null repeats forever. */
  repeatCount: number | null;
  occurrencesCreated: number;
  /** The day the cadence counts from. */
  startDate: string;
  /** The due date as set up, before any weekend shift; null without one. */
  dueDate: string | null;
  /** Each occurrence re-applies the template's due-date rule instead. */
  dueFromRule: boolean;
  avoidWeekends: boolean;
  nextOccurrenceOn: string | null;
  status: WorkflowScheduleStatus;
  canStop: boolean;
  canEdit: boolean;
  template: { pid: string; name: string } | null;
  taxpayer: { pid: string; name: string } | null;
  latestOccurrence: ScheduleWorkflow | null;
  upcoming: ScheduleOccurrence[];
};

/** A workflow's own schedule, with the workflow's position in it. */
export type WorkflowOccurrenceSchedule = WorkflowSchedule & {
  occurrenceNumber: number | null;
};

export type Workflow = {
  pid: string;
  name: string;
  description: string;
  dueDate: string | null;
  startDate: string | null;
  opensOn: string | null;
  avoidWeekends: boolean;
  taxpayerName: string;
  taxpayerPid: string;
  taxpayerType: string;
  createdAt: string;
  updatedAt: string | null;
  latestClientActivity: string | null;
  latestTeamActivity: string | null;
  progress: { total: number; done: number; blocked: number };
  currentTask: WorkflowCurrentTask | null;
  completedAt: string | null;
  canceledAt: string | null;
  canReopen: boolean;
  tags?: WorkflowTag[];
  schedule?: WorkflowOccurrenceSchedule | null;
  /** The template the workflow was created from; null once it is deleted. */
  template?: { pid: string; name: string } | null;
  /** The template's type, or 'other' without one; present when the blueprint is loaded. */
  workflowType?: WorkflowType;
};

export type BlueprintTaskTypeOption = {
  value: string;
  label: string;
  description?: string;
};

export type TaskBlueprintLink = {
  pid?: string;
  url: string;
  title: string;
};

export type TaskBlueprintFile = {
  pid: string;
  originalName: string;
  mimeType: string;
  size: number;
};

export type TaskBlueprintDocumentRequest = {
  pid?: string;
  name: string;
};

export enum TaskBlueprintInitialState {
  Open = 'open',
  Blocked = 'blocked',
}

export type MeetingPrepTimePeriod =
  | 'year_to_date'
  | 'trailing_12_months'
  | 'last_quarter'
  | 'since_last_meeting_brief'
  | 'all_time'
  | 'custom';

export type MeetingPrepScope = 'this_client' | 'client_group';

/** Mirrors App\Enums\AnalysisScope. */
export type AnalysisScope = 'this_client' | 'client_group';

/**
 * Analysis configuration carried by a Run analysis task blueprint and by a saved
 * (catalog) analysis task (HWEPD-10220).
 *
 * `promptKey` references the firm's prompt registry rather than a stored prompt,
 * and is null until the advisor picks one. Required-input rows exist in the data
 * model (HWEPD-10207) but the template editor does not collect them yet, so they
 * are deliberately absent here.
 */
export type AnalysisTaskConfig = {
  promptKey: string | null;
  scope: AnalysisScope;
  /**
   * The named window the analysis reads (HWEPD-10220). A template cannot express
   * "year to date" as two fixed dates — it runs later, for clients it was not
   * written against — so the period is what it stores and the dates below are
   * only its custom case.
   */
  timePeriod: MeetingPrepTimePeriod;
  /** yyyy-MM-dd; the custom range, set only when timePeriod is 'custom'. */
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
};

/** One prompt the firm can run, as offered by the template editor's picker. */
export type AnalysisPromptOption = {
  key: string;
  label: string;
  description: string | null;
};

/** The firm's analysis prompts grouped by family, for the prompt picker. */
export type AnalysisPromptGroup = {
  key: string;
  label: string;
  prompts: AnalysisPromptOption[];
};

/**
 * One document a Run analysis task expects. `documentType` is free text, as
 * document requests are; the period is a year plus a quarter when the document
 * is quarterly, and both are null when any period will do.
 */
export type AnalysisRequiredInput = {
  pid: string;
  documentType: string;
  periodYear: number | null;
  periodQuarter: number | null;
};

/**
 * The analysis configuration a live Run analysis task carries (HWEPD-10207),
 * copied from the template it was created from.
 *
 * `promptKey` references the firm's prompt registry rather than a stored prompt,
 * so a key that no longer resolves is possible and callers should tolerate it.
 */
export type TaskAnalysis = {
  promptKey: string | null;
  scope: AnalysisScope;
  /**
   * The named window the analysis reads (HWEPD-10220). Reuses the prep period
   * vocabulary because the resolver is shared, minus 'since_last_meeting_brief'
   * which is rejected for an analysis.
   */
  timePeriod: MeetingPrepTimePeriod;
  /** yyyy-MM-dd; the custom range, set only when timePeriod is 'custom'. */
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
  /** Absent when the caller did not load them — not the same as "none". */
  requiredInputs?: AnalysisRequiredInput[];
};

/**
 * Meeting Prep scoping config carried on a Prep task blueprint. Frontend-only
 * for now (HWEPD-10055): it survives edit/reorder within a session but is not
 * yet persisted server-side — a follow-up backend ticket promotes this to a
 * real MEETING_PREP task type + columns. Presence of this field is the
 * in-session discriminator for "this blueprint is a Prep task".
 */
export type MeetingPrepConfig = {
  timePeriod: MeetingPrepTimePeriod;
  /** yyyy-MM-dd, only set when timePeriod is 'custom'. */
  timePeriodStart?: string | null;
  timePeriodEnd?: string | null;
  scope: MeetingPrepScope;
  /**
   * Which brief this prep task produces (HWEPD-10259). Optional on the wire:
   * the column defaults to 'standard', so a task authored before meeting types
   * existed carries no value and is a standard prep.
   */
  meetingType?: MeetingType;
};

/**
 * The kind of meeting a Prep task is preparing for. Mirrors
 * App\Enums\MeetingType — the advisor picks it on the template. Every type runs
 * the same prompt and payload shape; year-end adds the projections widget.
 */
export type MeetingType = 'standard' | 'year_end';

/**
 * Category assigned to each ranked agenda item in a meeting-prep briefing.
 * Mirrors App\Enums\AgendaItemCategory — frozen as part of the briefing
 * output contract (schema_version 2).
 */
export type AgendaItemCategory =
  | 'discuss'
  | 'plan'
  | 'decide'
  | 'review'
  | 'flag';

/**
 * A grounding citation on a briefing item. Carries pids / chunk ids only —
 * never filenames or content excerpts (see BriefingOutputContract).
 */
export type BriefingCitation = {
  type: 'explicit' | 'kb_chunk';
  sourceType:
    | 'note'
    | 'file'
    | 'socrates_file'
    | 'client_profile'
    | 'projection';
  sourcePid: string;
  /** Present only for kb_chunk citations. */
  chunkId?: string;
};

/**
 * A citation's source resolved for display (HWEPD-10135): a human label and a
 * link to the owning record's tab. `url` is null when the source has no
 * navigable destination (e.g. a Socrates file).
 */
export type BriefSource = {
  type: 'note' | 'file' | 'socrates_file' | 'client_profile' | 'projection';
  label: string;
  url: string | null;
};

/**
 * Map of citation source_pid → resolved source, supplied alongside the briefs
 * (the payload itself stays opaque pids). A pid absent from the map is
 * unresolvable (deleted / out of scope) and renders as "source unavailable".
 */
export type BriefSources = Record<string, BriefSource>;

export type BriefingAgendaItem = {
  id: string;
  title: string;
  category: AgendaItemCategory;
  rationale: string;
  citations: BriefingCitation[];
};

export type BriefingOpenThread = {
  id: string;
  title: string;
  citations: BriefingCitation[];
};

export type BriefingSummary = {
  text: string;
  /** May be empty; never absent. */
  goals: string[];
};

export type BriefingFlags = {
  /** True when generated from partial context (relaxes the agenda minimum). */
  degraded: boolean;
  /** True when output was truncated against the token budget. */
  truncated: boolean;
};

/**
 * Structured meeting-prep briefing body, as serialized for the frontend.
 * Mirrors App\Services\MeetingPrep\BriefingOutputContract with its snake_case
 * keys mapped to camelCase by the MeetingBriefResource. One shape for every
 * MeetingType: a year-end brief is the regular brief, and the projections
 * widget it adds is page data the UI draws, not payload content.
 */
export type BriefingPayload = {
  schemaVersion: 2;
  summary: BriefingSummary;
  /** Order IS the rank (index 0 is most important). */
  agenda: BriefingAgendaItem[];
  conversationStarters: string[];
  openThreads: BriefingOpenThread[];
  flags: BriefingFlags;
};

/**
 * The brief's provenance header (HWEPD-10426): the same TYPE / WORKFLOW /
 * FOR / RUN grid the Analyses tab shows, so a brief and an analysis read as
 * one output. The client's name is no longer part of it — the accordion row
 * title carries it, and the roadmap link moved into the brief's "Where things
 * stand" section.
 */
export type BriefHeader = {
  /**
   * The meeting type the brief was run as, as its label — "Standard" or
   * "Year-end planning". A run predating meeting types takes its label from
   * the payload, or standard when it has no payload.
   */
  type: string;
  workflow: { pid: string; name: string } | null;
  /** Who the brief was written for; null when the run recorded no audience. */
  briefFor: AnalysisRanFor | null;
  /** When the brief was generated, ISO-8601; falls back to its creation time. */
  generatedAt: string | null;
  /** The advisor who ran it. Always null for pre-ledger briefs. */
  createdBy: string | null;
  taxpayerName: string;
  taxpayerPid: string;
};

/**
 * The headline figures of the client's latest reviewed tax projection
 * (HWEPD-10426). The brief links out to the Projections tab rather than
 * restating it, so this is three numbers and the year they belong to. Each
 * figure is null when it was never extracted — rendered as "—", never guessed.
 * The whole prop is null with no completed projection, when the latest one
 * yielded no headline figure at all, or with projections_feature_flag off (the
 * Projections tab route is gated by it).
 */
export type BriefProjectionSummary = {
  taxYear: number;
  priorYear: number;
  projectedLiability: number | null;
  /** Projected liability less the prior year's total tax. */
  vsPriorYear: number | null;
  effectiveRate: number | null;
};

/** One analysis run of a briefed workflow, linked into the Analyses tab. */
export type BriefWorkflowAnalysis = {
  pid: string;
  title: string;
  generatedAt: string | null;
};

/**
 * Lifecycle status of a briefing run. Mirrors App\Enums\BriefingRunStatus.
 * SUCCESS/DEGRADED carry a payload; EMPTY/FAILED do not (the UI branches on
 * status and surfaces failureReason).
 */
export type BriefingRunStatus =
  | 'running'
  | 'success'
  | 'empty'
  | 'failed'
  | 'degraded';

/** Thumbs an advisor leaves on any AI output. Mirrors App\Enums\AiFeedbackSentiment. */
export type FeedbackSentiment = 'positive' | 'negative';

/**
 * The requesting advisor's feedback on one AI output version (HWEPD-10185/10206):
 * a thumb, structured reason tags ("what worked / what went wrong"), and an
 * optional note. Mirrors App\Models\AiOutputFeedback::toApiArray. Null when the
 * advisor has left none. `reasons` is always an array (empty, never null).
 */
export type Feedback = {
  sentiment: FeedbackSentiment | null;
  reasons: string[];
  note: string | null;
  updatedAt: string | null;
};

/** Add/removed counts for an id-less string list (goals, conversation starters). */
export type BriefStringListDelta = { added: number; removed: number };

/** Item-level change sets for an id-keyed section, keyed by item id. */
export type BriefStructuredDelta = {
  added: string[];
  removed: string[];
  edited: string[];
};

/**
 * What the advisor changed relative to the generated baseline (HWEPD-10069),
 * derived by App\Services\MeetingPrep\BriefEditDiff. Null until the brief is
 * edited. The gap between generated and edited is the signal for improving the
 * prompt over time.
 */
export type BriefEditSummary = {
  summary: { textChanged: boolean; goals: BriefStringListDelta };
  agenda: BriefStructuredDelta & { reordered: boolean };
  conversationStarters: BriefStringListDelta;
  openThreads: BriefStructuredDelta;
  hasChanges: boolean;
};

/**
 * One briefing version in the client's Brief tab list (HWEPD-10056), as
 * serialized by App\Http\Resources\MeetingBriefResource. `payload` is null for
 * EMPTY/FAILED runs; the list branches on `status` and shows `failureReason`.
 * `payload` reflects the advisor's edits when present; `editSummary` describes
 * what changed and `myFeedback` carries the requesting advisor's feedback
 * (HWEPD-10069).
 */
export type BriefListItem = {
  pid: string;
  status: BriefingRunStatus;
  version: number;
  workflowPid: string | null;
  workflowName: string | null;
  /** The advisor who ran it; always null for pre-ledger briefs. */
  createdBy: string | null;
  /** Who the brief was written for (HWEPD-10426); null when unrecorded. */
  briefFor: AnalysisRanFor | null;
  scope: MeetingPrepScope | null;
  timePeriod: MeetingPrepTimePeriod | null;
  /**
   * Which brief this run produced, from the run's own parameters (HWEPD-10259).
   * Null for runs predating meeting types; the frontend then reads the
   * payload's own type, falling back to standard when there is no payload.
   */
  meetingType: MeetingType | null;
  isEdited: boolean;
  failureReason: string | null;
  generatedAt: string | null;
  createdAt: string | null;
  payload: BriefingPayload | null;
  editSummary: BriefEditSummary | null;
  myFeedback: Feedback | null;
};

export type TaskBlueprint = {
  pid: string;
  kind: 'client' | 'team';
  taskType: string | null;
  taskTypeLabel: string | null;
  title: string | null;
  description: string | null;
  initialState: TaskBlueprintInitialState;
  /** Opens with its order group but never holds the next group back. */
  isOptional?: boolean;
  orderNumber?: number;
  dueOffset?: TaskDateOffset;
  links?: TaskBlueprintLink[];
  files?: globalThis.File[];
  existingFiles?: TaskBlueprintFile[];
  existingFilePids?: string[];
  documentRequests?: TaskBlueprintDocumentRequest[];
  saveForFutureUse?: boolean;
  dueDate?: string | null;
  ownerPid?: string | null;
  jobFunctionId?: string | null;
  jobFunction?: JobFunction | null;
  meetingPrep?: MeetingPrepConfig;
  teamUpload?: TeamUploadConfig | null;
  analysis?: AnalysisTaskConfig | null;
  questionnaire?: QuestionnaireTaskConfig | null;
};

/** Template-side questionnaire config: which firm questionnaire a task instantiates. */
export type QuestionnaireTaskConfig = {
  templatePid: string | null;
  templateName?: string | null;
};

export type QuestionnaireTemplateOption = {
  pid: string;
  name: string;
  kind: string;
  /** A shared Harness default rather than one of the firm's own. */
  isDefault: boolean;
  description: string | null;
  sectionCount: number;
  questionCount: number;
};

export type QuestionnaireTemplateUsage = {
  workflowBlueprints: { pid: string; name: string }[];
  catalogTasks: { pid: string; title: string }[];
  /** The firm's workflows (not canceled) with a questionnaire task made from it. */
  workflows: number;
};

/** A firm questionnaire or Harness default as listed in the library. */
export type QuestionnaireTemplateSummary = {
  pid: string;
  name: string;
  description: string | null;
  kind: string;
  kindLabel: string;
  isDefault: boolean;
  /** The Harness default this was customized from; null for defaults and from-scratch questionnaires. */
  sourceName: string | null;
  sectionCount: number;
  questionCount: number;
  version: number;
  hasDraft: boolean;
  /** A customized copy whose Harness default has published since the copy was made or last reviewed. */
  sourceUpdated: boolean;
  /** Firm users edit their own questionnaires; Harness defaults only Harness staff. */
  canEdit: boolean;
  usage: QuestionnaireTemplateUsage;
  updatedAt: string;
  createdAt: string;
};

/** The Harness default a copy was customized from, at its current version. */
export type QuestionnaireSourceDefault = {
  name: string;
  version: number;
  schema: QuestionnaireSchema;
  /** Published since this copy was made or last reviewed. */
  updated: boolean;
};

/** A questionnaire's detail page: the summary plus its published schema. */
export type QuestionnaireTemplateDetail = QuestionnaireTemplateSummary & {
  schema: QuestionnaireSchema;
};

/** A firm questionnaire opened in the builder: the published schema plus the autosaved draft, if any. */
export type QuestionnaireTemplateEditable = QuestionnaireTemplateDetail & {
  draftSchema: QuestionnaireSchema | null;
  draftSavedAt: string | null;
  /** Bumped by every draft save, publish and discard; a request from an older revision is refused. */
  draftRevision: number;
};

/** One published version in a questionnaire's history; publishedBy is null for seeded versions. */
export type QuestionnaireTemplateVersion = {
  version: number;
  publishedAt: string;
  publishedBy: string | null;
};

export type QuestionnaireChangedQuestion = {
  key: string;
  text: string;
  section: string;
};

/** What publishing the saved draft would change, and anything that blocks it. */
export type QuestionnairePublishPreview = {
  /** The version publishing creates. */
  version: number;
  /** Never published before, so `changes` lists everything in it. */
  firstPublish: boolean;
  revision: number;
  changes: {
    added: QuestionnaireChangedQuestion[];
    removed: QuestionnaireChangedQuestion[];
    reworded: QuestionnaireChangedQuestion[];
    changed: QuestionnaireChangedQuestion[];
    rules: {
      key: string;
      text: string;
      whenKey: string;
      whenText: string;
      in: string[];
    }[];
    rulesRemoved: QuestionnaireChangedQuestion[];
    documentRequests: { key: string; text: string; name: string }[];
    documentRequestsRemoved: { key: string; text: string; name: string }[];
  };
  problems: string[];
  /** Clients mid-way through the questionnaire, who keep the version they started on. */
  openInstanceCount: number;
};

/** Live questionnaire state on a Task instance (advisor view). */
export type TaskQuestionnaire = {
  pid: string;
  templateName: string | null;
  templateVersion: number | null;
  completedAt: string | null;
  startedAt: string | null;
  lastActiveSection: string | null;
  prefilled: boolean;
  progress: {
    answered: number;
    total: number;
    percent: number;
    sections: Record<
      string,
      { answered: number; total: number; percent: number }
    >;
  };
  /** Document requests the current answers would raise on submission. */
  raisedDocumentRequests: number;
};

export type QuestionnaireSheetStatus =
  | 'not_started'
  | 'in_progress'
  | 'submitted';

export type QuestionnaireSheetAnswerRow = {
  kind: 'answer';
  key: string;
  /** Question group within the section ("You", "Spouse", ...), when the template sets one. */
  group?: string | null;
  question: string;
  /** Formatted for display; sensitive answers arrive already masked. */
  answer: string | null;
  answered: boolean;
  sensitive: boolean;
  documentRequests: string[];
};

export type QuestionnaireSheetRepeaterRow = {
  kind: 'repeater';
  key: string;
  group?: string | null;
  question: string;
  itemLabel: string;
  answered: boolean;
  items: { index: number; rows: QuestionnaireSheetAnswerRow[] }[];
};

export type QuestionnaireSheetRow =
  | QuestionnaireSheetAnswerRow
  | QuestionnaireSheetRepeaterRow;

export type QuestionnaireSheetSection = {
  key: string;
  title: string;
  description: string | null;
  answered: number;
  total: number;
  rows: QuestionnaireSheetRow[];
};

/** Masked, read-only answer sheet the advisor sees for a questionnaire task. */
export type QuestionnaireAnswerSheet = {
  pid: string;
  taskPid: string;
  taskTitle: string;
  templateName: string | null;
  templateVersion: number | null;
  status: QuestionnaireSheetStatus;
  startedAt: string | null;
  completedAt: string | null;
  taskState: TaskState;
  answered: number;
  total: number;
  documentRequests: number;
  canReopen: boolean;
  sections: QuestionnaireSheetSection[];
};

export type CatalogTask = {
  pid: string;
  kind: 'client' | 'team';
  taskType: string;
  taskTypeLabel: string;
  title: string | null;
  description: string | null;
  links?: TaskBlueprintLink[];
  files?: TaskBlueprintFile[];
  meetingPrep?: MeetingPrepConfig | null;
  teamUpload?: TeamUploadConfig | null;
  analysis?: AnalysisTaskConfig | null;
  questionnaire?: QuestionnaireTaskConfig | null;
};

export type TaskLink = {
  pid: string;
  url: string;
  title: string;
};

export type TeamUploadFileVersion = 'draft' | 'final';

/** Editable metadata chips on a Team Upload task document. */
export type TeamUploadFileMetadata = {
  category?: string | null;
  documentCategory?: DocumentCategory | null;
  taxYear?: number | null;
  version?: TeamUploadFileVersion;
};

/** One uploaded document on a Team Upload task. */
export type TeamUploadFile = {
  pid: string;
  filePid: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedBy: string | null;
  uploadedAt: string | null;
  category: string | null;
  documentCategory: DocumentCategory | null;
  taxYear: number | null;
  version: TeamUploadFileVersion;
  sharedWithClient: boolean;
};

/** Team Upload action payload, present only on team upload task instances. */
export type TeamUploadState = {
  shareWithClient: boolean;
  /** Draft/final applied to the next upload; the file's own chip governs after. */
  defaultVersion: TeamUploadFileVersion;
  /** True when any file — current or superseded — sits in the client portal. */
  hasSharedVersion: boolean;
  /** Current files, oldest first. */
  files: TeamUploadFile[];
};

/** Template-side Team Upload config on a task blueprint / catalog task. */
export type TeamUploadConfig = {
  shareWithClient: boolean;
  defaultVersion?: TeamUploadFileVersion;
};

export type TaskFile = {
  pid: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt?: string | null;
};

export type TaskActions = {
  canEdit: boolean;
  canComplete: boolean;
  canDelete: boolean;
};

export type TaskDocumentRequestStatus =
  | 'outstanding'
  | 'uploaded'
  | 'not_relevant';

export type TaskDocumentRequest = {
  pid: string;
  name: string;
  status: TaskDocumentRequestStatus;
};

export type Task = {
  pid: string;
  title: string;
  description: string | null;
  state: TaskState;
  hold?: TaskHold | null;
  followUpFor?: FollowUpFor | null;
  ownerType: 'client' | 'team';
  dueDate: string | null;
  dueDateSource: TaskDueDateSource | null;
  dueOffset: TaskDateOffset;
  sortOrder: number;
  orderNumber: number | null;
  /** Opens with its order group but never holds the next group back. */
  isOptional: boolean;
  createdAt: string;
  /** Persisted BlueprintTaskType value; discriminates team-upload variants. */
  taskType?: string | null;
  isUploadTask: boolean;
  isMeetingPrepTask: boolean;
  isTeamUploadTask: boolean;
  /** Scoping config, present only on Prep for meeting task instances. */
  meetingPrep?: MeetingPrepConfig | null;
  /** Upload state, present only on Team upload task instances. */
  teamUpload?: TeamUploadState | null;
  isAnalysisTask: boolean;
  /** Analysis config, present only on Run analysis task instances. */
  analysis?: TaskAnalysis | null;
  isQuestionnaireTask: boolean;
  /** Questionnaire state, present only on Questionnaire task instances. */
  questionnaire?: TaskQuestionnaire | null;
  links?: TaskLink[];
  files?: TaskFile[];
  documentRequests?: TaskDocumentRequest[];
  commentsCount?: number;
  openCommentsCount?: number;
  owner?: {
    pid: string;
    fullName: string | null;
    profilePhotoUrl: string | null;
  } | null;
  actions: TaskActions;
};

export type CommentableType = 'task';

export type CommentData = {
  pid: string;
  body: string;
  createdAt: string;
  resolvedAt: string | null;
  editedAt: string | null;
  canEdit: boolean;
  canDelete: boolean;
  author: {
    pid: string;
    fullName: string | null;
    profilePhotoUrl: string | null;
  };
};

export type TaskState =
  | 'blocked'
  | 'open'
  | 'in_progress'
  | 'on_hold'
  | 'completed'
  | 'canceled';

export type FollowUpSuggestion = {
  title: string;
  description: string;
  /** A client suggestion the client answers by uploading items (HWEPD-10708). */
  taskType?: 'upload';
};

export type TaskHoldReasonOption = {
  value: string;
  label: string;
  /** Team follow-ups the hold modal suggests for this reason (HWEPD-10705). */
  suggestions?: FollowUpSuggestion[];
  /** Client follow-ups suggested for it (HWEPD-10706). */
  clientSuggestions?: FollowUpSuggestion[];
  /** The next step the hold modal marks as suggested for this reason. */
  suggestedNextStep?: 'team_task' | 'client_task' | 'just_hold';
};

/** Why a task is on hold; null unless its state is on_hold. */
export type TaskHold = {
  reasons: TaskHoldReasonOption[];
  heldAt: string | null;
  /** Open follow-ups that release the hold when done. */
  followUps?: { pid: string; title: string }[];
};

/** On a follow-up row: the held task it gets moving again, while that is on hold. */
export type FollowUpFor = {
  title: string;
  reasons: string;
  heldBy: string | null;
  /** In a chain (HWEPD-10707): this follow-up's step, of how many. */
  step?: number | null;
  of?: number | null;
  /** In a chain (HWEPD-10707): the follow-up this one opens when done. */
  next?: string | null;
  /** In a chain, while it waits its turn: the follow-up it waits for. */
  after?: string | null;
  /** Completing this one returns the held task to Ready to work. */
  releases?: boolean;
};

export type TeamTask = {
  pid: string;
  title: string;
  description: string | null;
  state: TaskState;
  hold?: TaskHold | null;
  dueDate: string | null;
  owner: {
    pid: string;
    fullName: string | null;
    profilePhotoUrl: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
};

export type TaskStats = {
  open: number;
  overdue: number;
  dueThisWeek: number;
  unassigned: number;
};

export type WorkflowStats = {
  total: number;
  waitingOnClient: number;
  waitingOnTeam: number;
  overdue: number;
  dueThisWeek: number;
  stale: number;
};
